import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { CheckSquare, Columns3, Download, ListFilter, MapPinned, Menu, MoreVertical, Search, UserRound, X } from 'lucide-react';
import { useDebounced } from '@/components/live';
import { listRows } from '@/api/resources';
import { isSupabaseConfigured } from '@/lib/supabase';
import { HEADER_COMPACT, useMediaQuery } from '@/lib/useMediaQuery';
import { useGridToolbar } from '@/components/gridToolbar';
import { HeaderOrgPicker, HeaderPageGuide, HeaderUserMenu } from '@/components/HeaderControls';

interface Hit { kind: 'farmer' | 'plot'; id: string; code: string; title: string; detail: string | null }

/**
 * Farmers and plots matching the header search. Read through the same api views as the grids,
 * so a user only ever finds what their role and places already let them see.
 */
async function findRecords(term: string): Promise<Hit[]> {
  const [farmers, plots] = await Promise.all([
    listRows<{ id: string; farmer_code: string; full_name: string; village_name: string | null }>('farmers', {
      search: { columns: ['full_name', 'farmer_code', 'mobile'], term }, limit: 6,
    }),
    listRows<{ id: string; plot_code: string; plot_name: string | null; farmer_name: string | null }>('farm_plots', {
      search: { columns: ['plot_code', 'plot_name', 'farmer_name'], term }, limit: 6,
    }),
  ]);
  return [
    ...farmers.map((row) => ({ kind: 'farmer' as const, id: row.id, code: row.farmer_code, title: row.full_name, detail: row.village_name })),
    ...plots.map((row) => ({ kind: 'plot' as const, id: row.id, code: row.plot_code, title: row.plot_name ?? row.plot_code, detail: row.farmer_name })),
  ];
}

/**
 * One button holding the tools that no longer fit (owner, 2026-09-26: "when we are smaler
 * screens, group the header icons on click open as dropdown").
 *
 * It closes on an outside click and on Escape, like the organisation and profile menus beside it,
 * because three popovers in one bar that each dismiss differently is three things to learn. The
 * menu is only rendered when open: its contents include the column list, and a hidden copy of that
 * would double every checkbox in the accessibility tree.
 */
function HeaderMore({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      if (box.current && !box.current.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="header-pop header-more" ref={box}>
      <button
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label="More tools"
        className="mobile-header-menu"
        onClick={() => setOpen((now) => !now)}
        title="More tools"
        type="button"
      >
        <MoreVertical size={18} />
      </button>
      {open && (
        // Clicking anything inside is a decision made: the menu closes, the way a menu should.
        <div className="header-menu header-more-menu" onClick={() => setOpen(false)} role="menu">
          {children}
        </div>
      )}
    </div>
  );
}

/**
 * The top bar.
 *
 * On a phone or tablet it carries the menu button, top left, where a hamburger has meant "menu"
 * for fifteen years. On every screen size it carries search, top right (owner, 2026-09-21:
 * "Search icon - shift to the header on right"): an icon that opens the field, so the bar stays
 * quiet until someone actually wants to search.
 *
 * The page name sits in the bar (owner, 2026-09-21), taken from the menu entry so the two can
 * never disagree. The logo and the organisation's name live in the menu instead; the banner
 * still appears whenever a platform operator is working inside a customer organisation.
 */
export function MobileHeader({
  title,
  onOpenMenu,
  search,
  onSearch,
}: {
  title: string;
  onOpenMenu: () => void;
  search: string;
  onSearch: (value: string) => void;
}) {
  const [open, setOpen] = useState(Boolean(search));
  const input = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const term = useDebounced(search).trim();

  const hits = useQuery({
    queryKey: ['header-search', term],
    queryFn: () => findRecords(term),
    enabled: isSupabaseConfigured && open && term.length >= 2,
  });

  function go(hit: Hit) {
    // The register opens already filtered to the record, whose code is unique.
    navigate(`/${hit.kind === 'farmer' ? 'farmers' : 'plots'}?q=${encodeURIComponent(hit.code)}`);
    close();
  }

  const { controls } = useGridToolbar();
  const [chooser, setChooser] = useState(false);
  const chooserRef = useRef<HTMLDivElement>(null);
  // Narrow enough that seven icons would crush the page title to an ellipsis, which is what a
  // 430px window was doing: "O..." beside a full row of tools.
  const compact = useMediaQuery(HEADER_COMPACT);

  useEffect(() => {
    if (open) input.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!chooser) return;
    const onDown = (event: MouseEvent) => {
      if (chooserRef.current && !chooserRef.current.contains(event.target as Node)) setChooser(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [chooser]);

  function close() {
    onSearch('');
    controls?.setSearch('');
    setOpen(false);
  }

  return (
    <header className="mobile-header">
      <button aria-label="Open menu" className="mobile-header-menu" onClick={onOpenMenu} type="button">
        <Menu size={20} />
      </button>
      <h1 className="app-header-title">{title}</h1>

      <div className={`header-search${open ? ' open' : ''}`}>
        {open && (
          <input
            aria-label="Search"
            onChange={(event) => (controls ? controls.setSearch(event.target.value) : onSearch(event.target.value))}
            onKeyDown={(event) => { if (event.key === 'Escape') close(); }}
            /*
             * ONE SEARCH BOX, and it searches what is in front of you (owner, 2026-09-25: "Drop
             * the search on datagrid as we have in header"). On a page with a grid this filters
             * THAT grid - the header's own finder only ever knew farmers and plots, so dropping
             * the grid's box without this would have left thirty registers with no way to search
             * at all. With no grid on the page it is the finder it always was.
             */
            placeholder={controls ? controls.searchPlaceholder : 'Farmer, plot, docket, party...'}
            ref={input}
            value={controls ? controls.search : search}
          />
        )}
        <button
          aria-label={open ? 'Close search' : 'Search'}
          className="mobile-header-menu"
          onClick={() => (open ? close() : setOpen(true))}
          title={open ? 'Close search' : controls ? `Search ${controls.searchPlaceholder.replace(/^Search /i, '')}` : 'Search'}
          type="button"
        >
          {open ? <X size={18} /> : <Search size={18} />}
        </button>
        {open && !controls && term.length >= 2 && (
          <div className="header-search-results" role="listbox">
            {hits.isLoading && <p className="muted">Searching...</p>}
            {hits.data?.length === 0 && <p className="muted">No farmer or plot matches "{term}".</p>}
            {hits.data?.map((hit) => (
              <button key={hit.kind + hit.id} onClick={() => go(hit)} role="option" type="button">
                {hit.kind === 'farmer' ? <UserRound size={16} /> : <MapPinned size={16} />}
                <span><strong>{hit.title}</strong><small>{hit.code}{hit.detail ? ` - ${hit.detail}` : ''}</small></span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/*
        * The grid's controls, after the search icon (owner, 2026-09-25: "shift the columns and
        * export icon to the header after search icon"). They appear only on a page that HAS a
        * grid, and each only when that grid offers it: no export without the export permission,
        * no select without rows to select.
        *
        * NARROW: these and the page guide move inside one menu as labelled rows. The same
        * controls, drawn differently - not a second copy hidden with CSS, which would put every
        * checkbox in the document twice and leave tooltips on buttons nobody can see. A label
        * also says what the icon only hinted at, which is worth having on the small screen where
        * there is least room to guess.
        */}
      {compact ? (
        <HeaderMore>
          {controls && (
            <>
              <div className="header-menu-title">This table</div>
              <button
                aria-pressed={controls.filtering.active}
                className={`header-more-item${controls.filtering.active ? ' is-on' : ''}`}
                onClick={() => controls.filtering.setActive(!controls.filtering.active)}
                role="menuitem"
                type="button"
              >
                <ListFilter size={16} />
                <span>{controls.filtering.active ? 'Hide column filters' : 'Filter columns'}</span>
                {controls.filtering.count > 0 && <span className="header-count">{controls.filtering.count}</span>}
              </button>
              {controls.selection && (
                <button
                  aria-pressed={controls.selection.active}
                  className={`header-more-item${controls.selection.active ? ' is-on' : ''}`}
                  onClick={() => controls.selection!.setActive(!controls.selection!.active)}
                  role="menuitem"
                  type="button"
                >
                  <CheckSquare size={16} />
                  <span>{controls.selection.active ? 'Stop selecting rows' : 'Select rows'}</span>
                  {controls.selection.count > 0 && <span className="header-count">{controls.selection.count}</span>}
                </button>
              )}
              {controls.exportCsv && (
                <button className="header-more-item" onClick={controls.exportCsv} role="menuitem" type="button">
                  <Download size={16} />
                  <span>{controls.selection?.count
                    ? `Download the ${controls.selection.count} selected`
                    : 'Download what is on screen'}</span>
                </button>
              )}
              <div className="header-menu-title">Columns</div>
              {/* The checkboxes stay put when tapped: choosing three columns should not mean
                  opening this menu three times. */}
              <div className="header-more-columns" onClick={(event) => event.stopPropagation()}>
                {controls.columns.map((column) => (
                  <label key={column.key}>
                    <input checked={!column.hidden} onChange={() => controls.toggleColumn(column.key)} type="checkbox" />
                    {column.label}
                  </label>
                ))}
              </div>
            </>
          )}
          <HeaderPageGuide asMenuItem />
        </HeaderMore>
      ) : (
        <>
          {controls && (
            <div className="header-grid-tools">
              <div className="dg-chooser" ref={chooserRef}>
                <button
                  aria-expanded={chooser}
                  aria-label="Choose columns"
                  className="mobile-header-menu"
                  onClick={() => setChooser((now) => !now)}
                  title="Choose columns"
                  type="button"
                >
                  <Columns3 size={18} />
                </button>
                {chooser && (
                  <div className="dg-menu dg-chooser-menu">
                    {controls.columns.map((column) => (
                      <label key={column.key}>
                        <input checked={!column.hidden} onChange={() => controls.toggleColumn(column.key)} type="checkbox" />
                        {column.label}
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <button
                aria-label={controls.filtering.active ? 'Hide column filters' : 'Filter columns'}
                aria-pressed={controls.filtering.active}
                className={`mobile-header-menu${controls.filtering.active ? ' is-on' : ''}`}
                onClick={() => controls.filtering.setActive(!controls.filtering.active)}
                title={controls.filtering.active
                  ? `Hide the column filters${controls.filtering.count ? ` (${controls.filtering.count} set)` : ''}`
                  : 'Filter columns'}
                type="button"
              >
                <ListFilter size={18} />
                {controls.filtering.count > 0 && <span className="header-count">{controls.filtering.count}</span>}
              </button>

              {controls.selection && (
                <button
                  aria-label={controls.selection.active ? 'Stop selecting rows' : 'Select rows'}
                  aria-pressed={controls.selection.active}
                  className={`mobile-header-menu${controls.selection.active ? ' is-on' : ''}`}
                  onClick={() => controls.selection!.setActive(!controls.selection!.active)}
                  title={controls.selection.active
                    ? `Stop selecting${controls.selection.count ? ` (${controls.selection.count} selected)` : ''}`
                    : 'Select rows to download or delete'}
                  type="button"
                >
                  <CheckSquare size={18} />
                  {controls.selection.count > 0 && <span className="header-count">{controls.selection.count}</span>}
                </button>
              )}

              {controls.exportCsv && (
                <button
                  aria-label="Download what is on screen"
                  className="mobile-header-menu"
                  onClick={controls.exportCsv}
                  title={controls.selection?.count
                    ? `Download the ${controls.selection.count} selected`
                    : 'Download what is on screen'}
                  type="button"
                >
                  <Download size={18} />
                </button>
              )}
            </div>
          )}
          <HeaderPageGuide />
        </>
      )}
      <HeaderOrgPicker />
      <HeaderUserMenu />
    </header>
  );
}
